import { useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { Box, Flex } from '@chakra-ui/react';
import ButtonProc from 'components/common/Buttons/ButtonProc';
import OrderConfigModal, { OrderFormValues } from 'components/Configurator/OrderConfigModal';
import { useUser } from 'contexts';
import { bucketUrl } from 'utils/files';
import { useCreateConfigRequest } from 'hooks';
import { getToken } from 'utils/auth';
import { socketUrl } from 'constants/sockets';
import { ActiveParameters, IQuoteJson, ISettings } from 'types';
import { Image } from 'components/UI';
import { outputFileNamesSpec } from 'constants/specifications';
import { ConfiguratorTab } from 'constants/configurator';
import QuoteModal from 'components/Configurator/QuoteModal';
import OptionsMenuQuoteButton from './OptionsMenuQuoteButton';
import { IParameter, IProductResource, IUserResource } from '../../../../../server/types';
import OptionsMenuItem from './OptionsMenuItem';
import ParameterGroup from '../ParameterGroup';

let socketId: string;

export type OptionsMenuProps = {
  authUser: IUserResource;
  modelDownloadUrl: string;
  extractParams?: (
    arg0?: string,
    arg1?: number,
    arg2?: string,
    arg3?: IProductResource,
    arg4?: object,
    arg5?: object,
    arg6?: string
  ) => void;
  product?: IProductResource;
  params: IParameter[];
  productState:
    | {
        refParams?: string[];
        hasReferenceParameters?: boolean;
        hasTexts?: boolean;
        hasSelects?: boolean;
      }
    | undefined;
  extractParamsCallback?: (arg0: {
    status?: string;
    url?: string;
    paramsObject?: object;
    bucket?: string;
    error?: string;
    reportUrl?: string;
  }) => void;
  renderSelectsMenu: boolean;
  updateValue: (arg0: { optionKey?: string | boolean; renderSelectsMenu?: boolean }) => void;
  disabled?: boolean;
  queueJob: (download?: boolean, item?: string, outputFileNamesActivity?: any) => void;
  valid: boolean;
  loading: boolean;
  onTabChange?: (tab: ConfiguratorTab) => void;
  invalidParams: string[];
  name?: string;
  rfq?: string | number | boolean;
  showDownloadMenuIcon: (arg0?: boolean) => void;
  jobsLoading?: boolean;
  errorExisted?: boolean;
  theme: ISettings;
  skipDownloading: boolean;
  productChanged?: boolean;
  resultFileJobId: string;
  selectedProduct: string;
  resultFileNameRef: string;
  quote?: IQuoteJson;
  quotePdfUrl: string;
  activeParameters: ActiveParameters;
  parameterUpdate: (params: object, updateProduct?: IProductResource | null) => Promise<void>;
};

const OptionsMenu = ({
  name,
  disabled,
  params,
  valid,
  loading,
  authUser,
  extractParams,
  parameterUpdate,
  productState,
  product,
  modelDownloadUrl,
  extractParamsCallback,
  renderSelectsMenu,
  invalidParams,
  onTabChange,
  updateValue,
  queueJob,
  rfq,
  theme,
  showDownloadMenuIcon,
  jobsLoading = false,
  errorExisted = false,
  skipDownloading = false,
  productChanged = false,
  resultFileJobId,
  selectedProduct,
  resultFileNameRef,
  quote,
  quotePdfUrl,
  activeParameters,
}: OptionsMenuProps) => {
  const [createConfigRequest] = useCreateConfigRequest();
  const [initialState, setUpdatedState] = useState({
    updating: false,
    image: '',
    userDisplayName: `${authUser?.firstName} ${authUser?.lastName}`,
    userEmail: authUser?.email,
  });

  const [changedParams, setChangedParams] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [dlBtnState, setDlBtnState] = useState(0);

  const [vbtnStartupdate, setvbtnStartupdate] = useState(false);
  const [vbtnDownloadClicked, setVbtnDownloadClicked] = useState(false);

  const [orderConfigModalOpen, setOrderConfigModalOpen] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [user] = useUser();

  const [cache] = useState<string[]>([]);

  const cacheModel = (modelLink: string) => {
    let modelUrl = false;

    if (modelDownloadUrl !== '' && cache.length === 0 && dlBtnState === 0) {
      cache.push(modelLink);
      return null;
    }
    if (cache.indexOf(modelLink) === -1 && modelLink !== '' && dlBtnState === 1) {
      cache.push(modelLink);
      modelUrl = true;
      return modelUrl;
    }

    return null;
  };

  const socketRef = useRef<ReturnType<typeof io>>();

  const outputFileNamesActivity = outputFileNamesSpec
    .filter(
      ({ id }) =>
        (id !== 'send-bom-flc' && (user?.role?.specifications ?? []).includes(id)) ||
        (id === 'send-bom-flc' &&
          (user?.role?.specifications ?? []).includes(id) &&
          !(user?.role?.specifications ?? []).includes('view-bom'))
    )
    .map(
      (spec) => ({
        activityObj: spec.activityObj[0],
        file: spec.files[0],
      }),
      {}
    );

  const handleExtractParams = async (paramName?: number, value?: string) => {
    const token = getToken();
    extractParams?.(token, paramName, value, product, params, productState?.refParams, socketId);

    setUpdatedState((prevInitialState) => ({
      ...prevInitialState,
      updating: true,
    }));
  };

  useEffect(() => {
    socketRef.current = io(socketUrl);

    return () => {
      socketRef.current?.disconnect();
    };
  }, []);

  const updateParam = (key: string | number, value: string | undefined) => {
    let currentParams = { ...activeParameters };

    const matchingDisplayNameValue =
      currentParams[key as number].inputType === 'select'
        ? Object.values(currentParams[key as number]?.values ?? []).find((item) => item.displayName === value)?.value
        : value;

    const paramValue =
      currentParams[key as number].type === 'numeric'
        ? `${matchingDisplayNameValue}`?.replace(/[^0-9,.,-]+/, '')
        : `${matchingDisplayNameValue}`;

    const param = {
      ...currentParams[key as number],
      value: paramValue,
    };

    currentParams = { ...currentParams, [key]: param };

    parameterUpdate(currentParams);
  };

  const handleSocketInit = () => {
    if (!socketRef.current) return;

    socketRef.current.off('Extract Params Callback');
    socketRef.current.on(
      'Extract Params Callback',
      (data: {
        status?: string;
        url?: string;
        paramsObject?: object;
        bucket?: string;
        error?: string;
        reportUrl?: string;
      }) => extractParamsCallback?.(data)
    );
  };

  useEffect(() => {
    handleSocketInit();
  }, [initialState]);

  useEffect(() => {
    if (productState && productState.hasReferenceParameters) {
      handleExtractParams();
    }
  }, []);

  const handleUpdateValue = (key: number, value: string | boolean | undefined, update: boolean) => {
    setChangedParams(true);
    showDownloadMenuIcon(false);

    if (productState && productState.hasReferenceParameters && (update === undefined || update)) {
      handleExtractParams(key, value as string);
    } else {
      updateParam(key, value as string);
    }
  };

  const renderNextButton = () => (
    <ButtonProc
      first="NEXT"
      second=""
      onClick={() => updateValue({ renderSelectsMenu: false })}
      isDisabled={disabled ?? false}
      variant="solid"
      size="sm"
      width="95%"
      mt={1}
    />
  );

  const handleShowDownloadsMenu = () => {
    if (skipDownloading) {
      showDownloadMenuIcon(true);

      onTabChange?.(ConfiguratorTab.DOWNLOADS);
      return;
    }

    const modelDownloadReady = cacheModel(modelDownloadUrl);

    if (!modelDownloadReady || errorExisted) return;

    onTabChange?.(ConfiguratorTab.DOWNLOADS);
  };

  useEffect(() => {
    if (loading) {
      setvbtnStartupdate(true);
      return;
    }

    if (!vbtnStartupdate) return;

    setvbtnStartupdate(false);

    if (!vbtnDownloadClicked) return;

    setDlBtnState(0);
    setVbtnDownloadClicked(false);
    showDownloadMenuIcon(true);
    handleShowDownloadsMenu();
  }, [loading]);

  useEffect(() => {
    if (errorExisted) setGenerating(false);
  }, [errorExisted]);
  const handleGenerateModels = () => {
    const item = rfq ? 'updateResult' : 'updateQuote';
    queueJob(false, item);
  };

  if (!product || !productState) {
    return <Flex p={4}>PLEASE SELECT A PRODUCT FROM THE PRODUCT MENU.</Flex>;
  }

  const generateCapture = () => {
    const body = {
      userId: authUser.id,
      productName: product.displayName,
      productId: product.id,
    };
    createConfigRequest(body);
  };

  const renderUpdateButton = (
    <ButtonProc
      first="GENERATE"
      second="MODEL"
      onClick={() => {
        generateCapture();

        setGenerating(true);
        setChangedParams(false);

        handleGenerateModels();
      }}
      isDisabled={!valid || dlBtnState === 1 || jobsLoading || (!changedParams && generating && !productChanged)}
      variant="solid"
      size="sm"
      width="95%"
    />
  );

  const renderQuoteButton = !rfq ? (
    <OptionsMenuQuoteButton
      first="VIEW"
      second="QUOTE"
      isDisabled={dlBtnState === 1 || !generating || changedParams || jobsLoading || productChanged}
      onClick={() => setQuoteModalOpen(true)}
      sx={{ w: '95%' }}
    />
  ) : null;

  const renderQuotationConfigurationBtn = (
    <OptionsMenuQuoteButton
      first={rfq ? 'REQUEST' : 'ORDER'}
      second={rfq ? 'QUOTATION' : 'CONFIGURATION'}
      onClick={() => {
        const fn = () => setOrderConfigModalOpen(true);
        fn();
      }}
      isDisabled={dlBtnState === 1 || !generating || changedParams || jobsLoading || productChanged}
      sx={{ w: '95%' }}
    />
  );

  const renderGoToDownloadsButton =
    theme?.hideGenerateDownloads !== 'true' ? (
      <ButtonProc
        first="GENERATE"
        second="DOWNLOADS"
        mt={1}
        onClick={() => {
          if (skipDownloading) {
            handleShowDownloadsMenu();
            return;
          }

          setDlBtnState(1);
          setVbtnDownloadClicked(true);
          queueJob(true, 'generateDownloads', outputFileNamesActivity);
        }}
        isDisabled={dlBtnState === 1 || !generating || changedParams || jobsLoading || productChanged}
        variant="solid"
        size="sm"
        width="95%"
      />
    ) : null;

  const renderInvalidParams = () => invalidParams.map((param: string) => <Box key={param}>{param}</Box>);

  const renderButton = () => {
    if (product.preload && productState) {
      if (renderSelectsMenu && !productState.hasTexts) {
        return (
          <Box>
            {renderUpdateButton}
            {renderQuoteButton}
            {renderQuotationConfigurationBtn}
            {renderGoToDownloadsButton}
          </Box>
        );
      }

      if (renderSelectsMenu && productState.hasTexts) {
        return renderNextButton();
      }

      if (!renderSelectsMenu && productState.hasSelects) {
        return (
          <>
            {renderUpdateButton}
            {renderQuoteButton}
            {renderQuotationConfigurationBtn}
            {renderGoToDownloadsButton}
          </>
        );
      }
    }

    return (
      <>
        {renderUpdateButton}
        {renderQuoteButton}
        {renderQuotationConfigurationBtn}
        {renderGoToDownloadsButton}
      </>
    );
  };

  const paramKeys = product.parameterList;

  const renderParameters = paramKeys?.map(({ title, pList }, index) => (
    <ParameterGroup
      buttonProps={{
        color: 'primaryFontColour.500',
        borderBottom: 1,
        bgColor: 'base2Colour.400',
        borderColor: 'base2Colour.500',
        _hover: {
          bgColor: 'base2Colour.500',
        },
        textTransform: 'uppercase',
      }}
      boxProps={{
        px: 2,
      }}
      panelProps={{
        motionProps: {
          style: {
            overflow: 'visible',
          },
        },
      }}
      paramKeys={paramKeys}
      title={title}
      key={title}
    >
      <OptionsMenuItem
        parameters={pList}
        params={params}
        product={product}
        renderSelectsMenu={renderSelectsMenu}
        handleUpdateValue={handleUpdateValue}
        isLastGroup={index === paramKeys.length - 1}
      />
    </ParameterGroup>
  ));

  const renderInvalidMessage = !valid ? (
    <Box p="0 10px 20px 10px" color="red">
      The following parameters are invalid:
      {renderInvalidParams()}
    </Box>
  ) : null;

  const note =
    !product.preload || !productState || !renderSelectsMenu || !productState.hasTexts
      ? 'Select options and generate configuration'
      : 'Select options and continue to next';

  return (
    <Flex
      sx={{
        flexDir: 'column',
        flexGrow: 1,
        overflowY: 'auto',
        pb: 2,
      }}
    >
      <Flex
        sx={{
          flexDir: 'column',
          alignItems: 'center',
          flexGrow: 1,
          w: 300,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            w: 2.5,
          },
          '&::-webkit-scrollbar-thumb': {
            bgColor: 'scrollbarThumbColour.400',
          },
        }}
      >
        <Box py={2} px={4} fontSize={13} color="base2FontColour.400">
          {note.toUpperCase()}
        </Box>
        {product.refImage ? (
          <Image src={`${bucketUrl}/${product.refImage}`} alt={name} h={200} w={200} alignSelf="center" />
        ) : null}
        <Flex flexDir="column" w="full" flexGrow={1} minHeight={0}>
          <Box w="full">{renderParameters}</Box>
          {renderInvalidMessage}
        </Flex>
      </Flex>

      <Box alignSelf="center" textAlign="center">
        {renderButton()}
      </Box>
      <OrderConfigModal
        data={(user as OrderFormValues) ?? null}
        viewParameters={params as IParameter[]}
        rfq={rfq}
        product={product}
        isOpen={orderConfigModalOpen}
        onClose={() => setOrderConfigModalOpen(false)}
        resultFileJobId={resultFileJobId}
        selectedProduct={selectedProduct}
        resultFileNameRef={resultFileNameRef}
      />

      <QuoteModal
        isOpen={quoteModalOpen}
        quote={quote}
        closePopup={() => setQuoteModalOpen(false)}
        quotePdfUrl={quotePdfUrl as string}
      />
    </Flex>
  );
};

export default OptionsMenu;
